import { API_URL, JWT_TOKEN, SHOP_ID } from '../utils/helpers';
import {
  Choice,
  LocationState,
  OrderToken,
  OtherSettings,
  PaymentMethod,
  RadioOption,
  Requirement,
  ReturnMethod,
  ReturnReason,
} from '../utils/types';
import { STANDALONE_QR_FLAG } from '../utils/qrEntry';
import { acceptedMethodIds } from '../utils/requirementActions';
import { detailsSettingsOf } from '../utils/portalDetails';
import {
  errorBodyOf,
  isPortalInactive,
  isWithdrawalDisabled,
  RefusalCode,
  refusalCodeOf,
} from './orderLookupService';

// #149 (extracted from OrderFormPage.continueWithOrder, behaviour unchanged):
// after a lookup the return flow needs the requirements, the settings, the
// reasons and the return / payment methods, then opens the products step.
// Used by the return lookup and by the case C continuation of a withdrawal.
// #154 (audit #3): no fail-open any more. With a WITHDRAWAL session of the
// order, `/rules/requirements` already skips what the case C return skips
// (return-api #397); a coded 403 there is a refusal, as for a return.
// (audit #5, Codex r1) every request classifies its failure the same way, by
// the HTTP status (never by whether the body parses): 410
// WITHDRAWAL_PORTAL_INACTIVE -> `portalInactive`, 403 -> `refused` with its
// status (and code, if any), a 4xx of the requirements with a body ->
// `refused` (the lookup shows its message); anything else throws
// (transient): a failed list is never an empty list.

export interface ReturnFlowOptions {
  proof: string | null;
  shop: string | null;
  standalone: boolean;
  // the credentials of the requests (default: the ones in memory)
  credentials?: { token: string; shopId: number };
}

export type ReturnFlowResult =
  | { kind: 'ok'; state: LocationState }
  | {
      kind: 'refused';
      status: number;
      code: RefusalCode | null;
      message: string | undefined;
    }
  | { kind: 'portalInactive' }
  | { kind: 'disabledByShop' };

export class ReturnFlowUnavailable extends Error {}

// internal: a typed failure of a request, turned into its result below
class FlowStop extends Error {
  constructor(readonly result: Exclude<ReturnFlowResult, { kind: 'ok' }>) {
    super(result.kind);
  }
}

const messageOf = (body: unknown): string | undefined => {
  const message =
    body && typeof body === 'object'
      ? (body as { message?: unknown }).message
      : undefined;
  return typeof message === 'string' ? message : undefined;
};

// the failure of `response` (not ok) of request `what`; `refuses4xx`: a 4xx
// with a body is a refusal too (the requirements, whose message the lookup
// shows)
const failureOf = async (
  what: string,
  response: Response,
  refuses4xx = false
): Promise<Error> => {
  const status = response.status;
  const body = await errorBodyOf(response);
  if (isWithdrawalDisabled(status, body))
    return new FlowStop({ kind: 'disabledByShop' });
  if (isPortalInactive(status, body)) {
    return new FlowStop({ kind: 'portalInactive' });
  }
  if (
    status === 403 ||
    (refuses4xx && status >= 400 && status < 500 && body !== null)
  ) {
    if (body !== null) console.error('Error:', body);
    return new FlowStop({
      kind: 'refused',
      status,
      code: refusalCodeOf(status, body),
      message: messageOf(body),
    });
  }
  return new ReturnFlowUnavailable(`${what}: ${status}`);
};

export const loadReturnFlow = async (
  orderToken: OrderToken,
  options: ReturnFlowOptions
): Promise<ReturnFlowResult> => {
  const token = options.credentials?.token ?? JWT_TOKEN;
  const shopId = String(options.credentials?.shopId ?? SHOP_ID);
  const headers = { Authorization: 'Bearer ' + token, 'Shop-Id': shopId };

  const fetchReturnReasons = async (): Promise<Choice[]> => {
    const choices: Choice[] = [];
    const response = await fetch(API_URL + '/return-reasons', {
      method: 'GET',
      headers,
    });
    if (!response.ok) throw await failureOf('return reasons', response);
    const jsonData: ReturnReason[] = await response.json();
    jsonData.forEach(function (reason) {
      choices.push({
        value: reason.id,
        label: reason.display_name,
        name: reason.name,
      });
    });
    return choices;
  };

  const fetchSettings = async (): Promise<OtherSettings> => {
    const response = await fetch(API_URL + `/settings`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) throw await failureOf('settings', response);
    return await response.json();
  };

  const convertAmount = async (
    cost: number,
    from: string,
    to: string
  ): Promise<number> => {
    const response = await fetch(
      API_URL +
        `/return-methods/convert-cost?cost=` +
        cost +
        '&from-currency=' +
        from +
        '&to-currency=' +
        to,
      { method: 'GET', headers }
    );
    if (!response.ok) throw await failureOf('convert cost', response);
    return await response.json();
  };

  const fetchReturnMethods = async (
    requirements: Requirement,
    currency: string
  ): Promise<RadioOption[]> => {
    const returnMethods: RadioOption[] = [];
    const response = await fetch(API_URL + '/return-methods?is-active=true', {
      method: 'GET',
      headers,
    });
    if (!response.ok) throw await failureOf('return methods', response);
    const jsonData: ReturnMethod[] = await response.json();
    // #132: every matching rule restricts at submit -> intersection of all
    // entries; an empty list offers nothing (notice on the preferences step)
    const accepted = acceptedMethodIds(
      requirements.actions,
      'SET_RETURN_METHODS'
    );
    for (const method of jsonData) {
      if (accepted === null || accepted.has(String(method.id))) {
        let costMethod = method.cost;
        if (currency !== orderToken.order_details.currency) {
          costMethod = await convertAmount(
            method.cost,
            currency,
            orderToken.order_details.currency
          );
        }
        returnMethods.push({
          id: method.id,
          label: method.name,
          value: method.short_instruction,
          cost: costMethod,
          more_details: method.display_pickup_address,
          show_popup: method.generate_awb && !method.display_pickup_address,
        });
      }
    }
    return returnMethods;
  };

  const fetchPaymentMethods = async (
    requirements: Requirement
  ): Promise<RadioOption[]> => {
    const paymentMethods: RadioOption[] = [];
    const response = await fetch(API_URL + '/payment-methods?is-active=true', {
      method: 'GET',
      headers,
    });
    if (!response.ok) throw await failureOf('payment methods', response);
    const jsonData: PaymentMethod[] = await response.json();
    // #132: same intersection rule as the return methods
    const accepted = acceptedMethodIds(
      requirements.actions,
      'SET_PAYMENT_METHODS'
    );
    jsonData.forEach(function (method) {
      if (accepted === null || accepted.has(String(method.id))) {
        paymentMethods.push({
          id: method.id,
          label: method.name,
          value: method.short_instruction,
          cost: 0,
          more_details: method.require_iban,
          ask_bank_name: method.ask_bank_name !== false,
          show_popup: false,
        });
      }
    });
    return paymentMethods;
  };

  let offerCorrelationId: string | null = null;
  let requirements: Requirement;
  let settings: OtherSettings;
  let returnReasons: Choice[];
  let returnMethods: RadioOption[];
  let paymentMethods: RadioOption[];
  try {
    const responseRequirements = await fetch(
      API_URL + '/rules/requirements?order-id=' + orderToken.order_details.id,
      { method: 'GET', headers }
    );
    // The order lookup already succeeded (2xx) here. #122: a 403 window /
    // rule denial carries its code, shown as a localized reason.
    if (!responseRequirements.ok) {
      throw await failureOf('requirements', responseRequirements, true);
    }
    offerCorrelationId =
      responseRequirements.headers?.get('X-Offer-Correlation') ?? null;
    requirements = await responseRequirements.json();
    settings = await fetchSettings();
    returnReasons = await fetchReturnReasons();
    returnMethods = await fetchReturnMethods(requirements, settings.currency);
    paymentMethods = await fetchPaymentMethods(requirements);
  } catch (error) {
    if (error instanceof FlowStop) return error.result;
    throw error;
  }
  // #496: the "Detalii" setting (an API without it: SHOWN / 500, as before)
  const details = detailsSettingsOf(settings);
  return {
    kind: 'ok',
    state: {
      order_token: orderToken,
      requirements,
      offer_correlation_id: offerCorrelationId,
      offer_session: offerCorrelationId
        ? {
            token,
            shopId: Number(shopId),
            orderId: orderToken.order_details.id,
          }
        : null,
      products_to_return: [],
      products_to_exchange: [],
      return_reasons: returnReasons,
      return_methods: returnMethods,
      payment_methods: paymentMethods,
      proof: options.proof,
      shop: options.shop as string,
      allow_exchanges: settings.allow_exchanges,
      allow_exchange_more_expensive: settings.allow_exchange_more_expensive,
      terms_and_conditions_url: settings.terms_and_conditions_url ?? null,
      portal_details_mode: details.mode,
      portal_details_max_length: details.maxLength,
      ...(options.standalone ? { [STANDALONE_QR_FLAG]: true } : {}),
    } as LocationState,
  };
};
